import {Avatar, Button, InfoTooltip, Link} from '@wandb/components';
import Markdown from '@wandb/weave/common/components/Markdown';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {addUTCTimezoneIfNotPresent} from '@wandb/weave/common/util/time';
import {useRunTraceStats} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {
  getTagContrastColor,
  RemovableTag,
  RemoveAction,
} from '@wandb/weave/components/Tag';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {Grid, Header, Image, Segment} from 'semantic-ui-react';

import {InlineMarkdownEditor} from '../../components/elements/InlineMarkdownEditor';
import {TimeDisplay} from '../../components/elements/TimeDisplay';
import {ErrorBoundary} from '../../components/ErrorBoundary';
import {useFetchRunJob} from '../../components/Launch/hooks';
import NoMatch from '../../components/NoMatch';
import {ReproduceRunDrawer} from '../../components/ReproduceRun/ReproduceRunDrawer';
import {RunConfigTree} from '../../components/RunConfigTree';
import {TabWaveLoader} from '../../components/RunDetails/TabWaveLoader';
import RunStateIndicator from '../../components/RunStateIndicator';
import {RunSummaryTree} from '../../components/RunSummaryTree';
import {RunTriggerEventsExport} from '../../components/RunTriggerEventsExport';
import {
  isParquetExportAllowed,
  RunTriggerParquetExport,
} from '../../components/RunTriggerParquetExport';
import {RunTriggerRecomputeKeysInfo} from '../../components/RunTriggerRecomputeKeysInfo';
import {RunTriggerRecomputeSystemMetrics} from '../../components/RunTriggerRecomputeSystemMetrics';
import {ServiceAccountModal} from '../../components/ServiceAccountModal';
import {SlurmJobDrawer} from '../../components/Slurm/SlurmJobDrawer/SlurmJobDrawer';
import {useSlurmJobActions} from '../../components/Slurm/useSlurmJobActions';
import {ViewInGrafanaButton} from '../../components/Slurm/ViewInGrafanaButton';
import {TagAddButton} from '../../components/TagAddButton';
import {useRunOverviewQuery} from '../../generated/graphql';
import {captureError} from '../../integrations';
import {useOnPressAnalytics} from '../../services/analytics';
import {useViewer} from '../../state/viewer/hooks';
import {useAdminModeActive} from '../../util/admin';
import {DateFormat, format} from '../../util/date';
import DOC_URLS from '../../util/doc_urls';
import {propagateErrorsContext} from '../../util/errors';
import type * as RunTypes from '../../util/runTypes';
import {checkIsServiceAccount} from '../../util/serviceAccounts';
import * as urls from '../../util/urls';
import {RawCodeModal} from './RawCodeModal';
import {RunArtifactGrid} from './RunArtifacts';
import {RunOverviewCommandItems} from './RunOverviewCommandItems';
import {RunOverviewItem} from './RunOverviewItem';
import {RunOverviewPatchItem} from './RunOverviewPatchItem';
import {SdkVersionTag} from './SdkVersionTag';
import {useUpdateRun} from './useUpdateRun';

interface RunOverviewProps {
  entityName: string;
  projectName: string;
  runName: string;
  config: RunTypes.KeyVal;
  summaryMetrics: RunTypes.KeyVal;

  history: any;
}

export function getPinnedConfigValue(
  config: RunTypes.KeyVal,
  key: string
): unknown | undefined {
  const entry = config[key];
  if (entry === undefined) {
    return undefined;
  }
  // Config values are stored with a {desc, value} wrapper
  if (entry != null && typeof entry === 'object' && 'value' in entry) {
    return (entry as any).value;
  }
  return entry;
}

export function extractPinnedKeys(config: RunTypes.KeyVal): string[] {
  const wandb = config._wandb as any;
  if (wandb?.value?.pinned_keys && Array.isArray(wandb.value.pinned_keys)) {
    return wandb.value.pinned_keys;
  }
  return [];
}

function isOffline(wandbValue: any) {
  // This is a helper function that de-codes the telemetry data provided in the config from the client
  // For details of the structure see: https://github.com/wandb/client/blob/master/wandb/proto/wandb_telemetry.proto
  // To summarize:  - `t` is the telemetry record
  //                - `3` is the feature field (in the telemetry record)
  //                - `4` is the offline flag (in the feature message)
  return !!wandbValue?.t?.[3]?.[4];
}

function getRawJsonDisplayText(json: any): string {
  const stringifiedJson = JSON.stringify(json);

  return stringifiedJson.length < 1000000
    ? JSON.stringify(json, null, 2)
    : stringifiedJson;
}

function RunOverview(props: RunOverviewProps) {
  return (
    <ErrorBoundary
      onError={(error, info) =>
        captureError(error, 'RunOverview ErrorBoundary error', {extra: info})
      }
      renderError={() => <NoMatch />}>
      <RunOverviewInner {...props} />
    </ErrorBoundary>
  );
}

function RunOverviewInner({
  entityName,
  projectName,
  runName,
  config,
  summaryMetrics,
}: RunOverviewProps) {
  const viewer = useViewer();
  const runOverviewQuery = useRunOverviewQuery({
    variables: {
      entityName,
      projectName: projectName ?? '',
      runName: runName ?? '',
    },
    // Keep partial data when individual fields (e.g. patchFiles) error
    // server-side — the consumers below already guard against null.
    // propagateErrorsContext prevents the global apollo errorLink from
    // surfacing a page-level error.
    errorPolicy: 'all',
    context: propagateErrorsContext(),
  });

  // Apollo 3.8+ deprecated onError on useQuery (fires unreliably), so we
  // surface query errors via a useEffect on the resolved error reference.
  // We keep rendering whatever data came back (errorPolicy: 'all' gives us
  // partial data; null guards below handle missing fields) and just let
  // the user know that some fields may be missing.
  useEffect(() => {
    if (runOverviewQuery.error) {
      toast.error(
        "Couldn't load all run overview data. Some fields may be missing."
      );
    }
  }, [runOverviewQuery.error]);

  const [reproduceDrawerOpen, setReproduceDrawerOpen] = useState(false);
  const onShowReproduceDrawer = useOnPressAnalytics(
    'Reproduce run',
    {
      action: 'show drawer',
      location: 'run overview',
    },
    () => setReproduceDrawerOpen(true)
  );

  const run = runOverviewQuery.data?.project?.run;
  const project = runOverviewQuery.data?.project;

  const {
    notes,
    state,
    stopped,
    heartbeatAt,
    config: runConfig,
    inputArtifacts,
    createdAt,
    jobType,
    group,
    tags = [],
    host,
    sweep,
    runInfo,
    user,
    branchPoint,
    name: runNameFromQuery = '',
  } = run ?? {};

  const {entityName: projectEntityName = '', name: projectNameFromQuery = ''} =
    project ?? {};

  const isAdmin = useAdminModeActive();
  const runTraceStats = useRunTraceStats(
    projectEntityName,
    projectNameFromQuery,
    runNameFromQuery
  );

  // Get the job that created this run (if any)
  const {jobInfo} = useFetchRunJob({
    entityName: projectEntityName,
    projectName: projectNameFromQuery,
    runName: runNameFromQuery,
    skip: inputArtifacts == null || inputArtifacts.totalCount === 0,
  });

  const [showRaw, setShowRaw] = useState<{header: string; json: any}>();
  const slurm = useSlurmJobActions({entityName, projectName, runName});
  const [isSlurmDrawerOpen, setSlurmDrawerOpen] = useState(false);

  const updateRun = useUpdateRun(run);

  if (runOverviewQuery.loading) {
    return <TabWaveLoader />;
  }

  if (!run || !project) {
    return <NoMatch />;
  }

  // If there are traces for this run, display a link to them.
  const traceCount = runTraceStats.result?.count ?? 0;
  let tracesLink: string | null = null;
  if (traceCount > 0) {
    const filterValue = `${project.internalId}:${runNameFromQuery}`;
    const runFilters = {
      items: [
        {
          field: 'wb_run_id',
          operator: '(string): equals',
          value: filterValue,
        },
      ],
    };
    const runCols = {
      wb_run_id: true,
    };
    // TODO: Constructing the URL this way isn't nice, but we don't have
    // access here to all of the routing context inside Weave.
    tracesLink = `/${project.entityName}/${
      project.name
    }/weave/traces?filters=${JSON.stringify(
      runFilters
    )}&cols=${JSON.stringify(runCols)}`;
  }

  const wandb = config._wandb as any;

  let cliVersion;
  let spellUrl;
  let startUTCSeconds;
  let runIsOffline = false;
  if (wandb && wandb.value) {
    cliVersion = wandb.value.cli_version;
    spellUrl = wandb.value.spell_url;
    startUTCSeconds = wandb.value.start_time;
    runIsOffline = isOffline(wandb.value);
  }

  const pinnedKeys = extractPinnedKeys(config);

  let createdTimeValue = createdAt;
  if (runIsOffline && startUTCSeconds) {
    createdTimeValue = new Date(startUTCSeconds * 1000)
      .toISOString()
      .split('.')[0];
  }

  const createdTime = format(
    new Date(addUTCTimezoneIfNotPresent(createdTimeValue ?? '')),
    DateFormat.MONTH_DAYTH_YEAR_HOUR_MIN_SECONDS
  );

  const readOnly = !viewer || (project.readOnly ?? false);

  // account for non-team entity case
  const serviceAccountModalLink = project.entity.isTeam
    ? urls.teamSettings(projectEntityName)
    : `/${projectEntityName}`;

  const systemStats = [
    runInfo?.gpuCount,
    runInfo?.gpu,
    runInfo?.cpuCount,
    runInfo?.cpuCountLogical,
  ];
  const applySystemHardwareStyles = _.compact(systemStats).length > 1;
  const runPath = `${projectEntityName}/${projectNameFromQuery}/${runNameFromQuery}`;

  return (
    <TailwindContents>
      <RawCodeModal
        header={showRaw?.header ?? null}
        isOpen={Boolean(showRaw)}
        json={showRaw?.json ?? null}
        onClose={() => setShowRaw(undefined)}
      />
      <ReproduceRunDrawer
        entityName={entityName}
        isOpen={reproduceDrawerOpen}
        onOpenChange={setReproduceDrawerOpen}
        projectName={projectName}
        runName={runName}
      />
      {slurm.showSlurmActions && (
        <SlurmJobDrawer
          open={isSlurmDrawerOpen}
          onClose={() => setSlurmDrawerOpen(false)}
          jobId={slurm.jobId}
          entityName={entityName}
          projectName={projectName}
          runName={runName}
          clusterName={slurm.clusterName ?? undefined}
          region={slurm.region ?? undefined}
          isRunLive={state === 'running'}
          readOnly={slurm.readOnly}
          grafanaUrl={slurm.grafanaUrl || undefined}
          metadata={slurm.metadata}
        />
      )}
      <div className="run-overview">
        <div className="run-overview-top">
          {pinnedKeys.some(
            key => getPinnedConfigValue(config, key) !== undefined
          ) && (
            <div className="overview-item" style={{alignItems: 'flex-start'}}>
              <div className="overview-key">References</div>
              <div className="overview-value pinned-references">
                {pinnedKeys.map(key => {
                  const value = getPinnedConfigValue(config, key);
                  if (value === undefined) {
                    return null;
                  }
                  const displayValue =
                    typeof value === 'string' ? value : JSON.stringify(value);
                  return (
                    <div key={`pinned-${key}`}>
                      {typeof value === 'string' ? (
                        <Markdown
                          content={`${key}: ${displayValue}`}
                          condensed={false}
                        />
                      ) : (
                        <span>
                          {key}: {displayValue}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">Notes</div>
            <div className="overview-value">
              <InlineMarkdownEditor
                readOnly={readOnly}
                placeholder="What makes this run special?"
                serverText={notes ?? ''}
                onBlur={newNotes => {
                  (async () => {
                    const result = await updateRun({notes: newNotes});
                    if (!result.success) {
                      toast.error(result.error);
                    }
                  })();
                }}
              />
            </div>
          </div>
          {(tags.length > 0 || !readOnly) && (
            <div className="overview-item mt-4">
              <div className="overview-key">Tags</div>
              <div className="overview-value">
                <div className="flex flex-wrap items-center gap-4">
                  {/* TO-DO: Swap over to core-ux `GroupTag`. Waiting on the team to add ToolTips for truncated Tags */}
                  {tags.map(tag => (
                    <div
                      key={tag.name}
                      className="shrink-0 [&>div]:whitespace-nowrap">
                      <RemovableTag
                        label={tag.name}
                        color={getTagContrastColor(tag.colorIndex)}
                        Wrapper={null}
                        removeAction={
                          <RemoveAction
                            onClick={async (e: React.MouseEvent) => {
                              e.stopPropagation();
                              const result = await updateRun({
                                tags: tags
                                  .map(t => t.name)
                                  .filter(n => n !== tag.name),
                              });
                              if (!result.success) {
                                toast.error(result.error);
                              }
                            }}
                          />
                        }
                      />
                    </div>
                  ))}
                  {!readOnly && (
                    <TagAddButton
                      compact
                      placement="bottom start"
                      tags={tags}
                      availableTags={project.tags ?? []}
                      addTag={async tag => {
                        if (
                          !_.includes(
                            tags.map(t => t.name),
                            tag.name
                          )
                        ) {
                          const result = await updateRun({
                            tags: [...tags.map(t => t.name), tag.name],
                          });
                          if (!result.success) {
                            toast.error(result.error);
                          }
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          <RunOverviewItem name="Author">
            {checkIsServiceAccount(user?.accountType ?? '') ? (
              <div className="user-link">
                <Avatar
                  src={user?.photoUrl ?? ''}
                  textValue={user?.username ?? ''}
                />
                <ServiceAccountModal
                  serviceAccountName={user?.name ?? ''}
                  teamMembersUrl={
                    readOnly ? undefined : serviceAccountModalLink
                  }
                />
              </div>
            ) : user?.username ? (
              <Link href={urls.profilePage(user.username)}>
                <Avatar src={user.photoUrl ?? ''} textValue={user.username} />
              </Link>
            ) : null}
          </RunOverviewItem>
          <div className="overview-item">
            <div className="overview-key">State</div>
            <div className="overview-value">
              <RunStateIndicator
                readOnly={readOnly}
                isSlurmRun={slurm.showSlurmActions}
                run={{
                  id: run.id ?? '',
                  config: runConfig ?? {},
                  state: state ?? '',
                  stopped: stopped ?? false,
                }}
              />
            </div>
          </div>
          {jobInfo != null && (
            <div className="overview-item">
              <div className="overview-key">Job</div>
              <div className="overview-value">
                <Link
                  href={urls.job({
                    entityName: projectEntityName,
                    projectName: projectNameFromQuery,
                    jobId: jobInfo.id,
                    jobVersion: jobInfo.versionStr,
                    jobTab: 'version_details',
                  })}>
                  {jobInfo?.versionedJobName}
                </Link>
              </div>
            </div>
          )}
          {branchPoint && (
            <div className="overview-item">
              <div className="overview-key">Forked From</div>
              <div className="overview-value">
                <Link
                  href={urls.run({
                    entityName: branchPoint?.run?.project?.entity?.name ?? '',
                    projectName: branchPoint?.run?.project?.name ?? '',
                    name: branchPoint?.run?.name ?? '',
                  })}>
                  {branchPoint.run.name} @ Step {branchPoint.step}
                </Link>
              </div>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">Start time</div>
            <div className="overview-value">{createdTime}</div>
          </div>
          {sweep != null && (
            <div className="overview-item">
              <div className="overview-key">Sweep</div>
              <div className="overview-value">
                <Link
                  href={urls.sweep({
                    entityName: projectEntityName,
                    projectName: projectNameFromQuery,
                    sweepName: sweep?.name ?? '',
                  })}>
                  {sweep.name}
                </Link>
              </div>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">
              Runtime
              <InfoTooltip
                id="runtime-tooltip"
                aria-label="Run duration (including idle time)">
                Total time from when the run started to when it last reported
                activity. May include idle time.
              </InfoTooltip>
            </div>
            <div className="overview-value" aria-describedby="runtime-tooltip">
              <TimeDisplay
                timestamp={new Date(run.createdAt ?? '')}
                format="month_round"
                now={new Date(heartbeatAt ?? '')}
              />
            </div>
          </div>
          <RunOverviewItem name="Run path" isCopyable>
            {runPath}
          </RunOverviewItem>
          {slurm.showSlurmActions && (
            <RunOverviewItem name="Slurm job">
              <div className="flex items-center gap-12">
                <button
                  type="button"
                  aria-label="View Slurm job details"
                  className="cursor-pointer border-none bg-transparent p-0 font-medium text-wb-text-link hover:text-wb-text-link-hover"
                  onClick={() => {
                    slurm.refetch();
                    setSlurmDrawerOpen(true);
                  }}>
                  {slurm.jobId}
                </button>
                {slurm.grafanaUrl && (
                  <ViewInGrafanaButton grafanaUrl={slurm.grafanaUrl} />
                )}
              </div>
            </RunOverviewItem>
          )}
          {host !== null && (
            <RunOverviewItem name="Hostname">{host}</RunOverviewItem>
          )}
          {runInfo && (
            <>
              {runInfo.os ? (
                <RunOverviewItem name="OS">{runInfo.os}</RunOverviewItem>
              ) : null}
              {runInfo.python ? (
                <RunOverviewItem name="Python version">
                  {runInfo.python}
                </RunOverviewItem>
              ) : null}
              <RunOverviewCommandItems
                run={run}
                onShowReproduceDrawer={onShowReproduceDrawer}
              />
              <RunOverviewPatchItem
                entityName={entityName}
                projectName={projectName}
                runName={runName}
              />
              {runInfo.colab && (
                <div className="overview-item">
                  <div className="overview-key">Colab link</div>
                  <div className="overview-value">
                    <TargetBlank href={runInfo.colab}>
                      {runInfo.colab}
                    </TargetBlank>
                  </div>
                </div>
              )}
              {(runInfo.cpuCount ||
                runInfo.gpuCount ||
                runInfo.gpu ||
                runInfo.cpuCountLogical) && (
                <div
                  style={
                    applySystemHardwareStyles
                      ? {alignItems: 'flex-start'}
                      : undefined
                  }
                  className="overview-item">
                  <div className="overview-key">System Hardware</div>
                  <div className="overview-value">
                    <table className="system-table">
                      <tbody>
                        {runInfo.cpuCount && (
                          <tr>
                            <td className="key">CPU count</td>
                            <td className="val">{runInfo.cpuCount}</td>
                          </tr>
                        )}
                        {runInfo.cpuCountLogical && (
                          <tr>
                            <td className="key">Logical CPU count </td>
                            <td className="val">{runInfo.cpuCountLogical}</td>
                          </tr>
                        )}
                        {runInfo.gpuCount && (
                          <tr>
                            <td className="key">GPU count</td>
                            <td className="val">{runInfo.gpuCount}</td>
                          </tr>
                        )}
                        {runInfo.gpu && (
                          <tr>
                            <td className="key">GPU type</td>
                            <td className="val">{runInfo.gpu}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          {cliVersion && (
            <div className="overview-item">
              <div className="overview-key">W&amp;B CLI Version</div>
              <div className="overview-value sdk-version-value">
                <span>{cliVersion}</span>
                <SdkVersionTag currentVersion={cliVersion} />
              </div>
            </div>
          )}
          {tracesLink && (
            <div className="overview-item">
              <div className="overview-key">Weave traces</div>
              <div className="overview-value">
                <Link href={tracesLink}>
                  {traceCount.toLocaleString()}{' '}
                  {maybePluralizeWord(traceCount, 'trace')}
                </Link>
              </div>
            </div>
          )}
          {spellUrl && (
            <div className="overview-item">
              <div className="overview-key">Spell Run</div>
              <div className="overview-value">
                <TargetBlank href={`${spellUrl}`}>{spellUrl}</TargetBlank>
              </div>
            </div>
          )}
          {group && (
            <div className="overview-item">
              <div className="overview-key">Group</div>
              <div className="overview-value">
                <Link
                  href={urls.runGroup({
                    entityName: projectEntityName,
                    projectName: projectNameFromQuery,
                    name: group,
                  })}>
                  {group}
                </Link>
              </div>
            </div>
          )}
          {jobType && (
            <div className="overview-item">
              <div className="overview-key">Job Type</div>
              <div className="overview-value">{jobType}</div>
            </div>
          )}
          {isParquetExportAllowed(isAdmin) && (
            <div className="overview-item">
              <div className="overview-key">Parquet Export</div>
              <div className="overview-value">
                <RunTriggerParquetExport runID={run.id ?? ''} />
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="overview-item">
              <div className="overview-key">Recompute KeysInfo</div>
              <div className="overview-value">
                <RunTriggerRecomputeKeysInfo runID={run.id ?? ''} />
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="overview-item">
              <div className="overview-key">Recompute System Metrics</div>
              <div className="overview-value">
                <RunTriggerRecomputeSystemMetrics runID={run.id ?? ''} />
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="overview-item">
              <div className="overview-key">Trigger Export Events</div>
              <div className="overview-value">
                <RunTriggerEventsExport runID={run.id ?? ''} />
              </div>
            </div>
          )}
        </div>
        <Grid stackable className="config-and-summary">
          <Grid.Row columns={2}>
            <Grid.Column>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}>
                <Header>Config</Header>
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() =>
                    setShowRaw({
                      header: `Config`,
                      json: getRawJsonDisplayText(config),
                    })
                  }>
                  View raw data
                </Button>
              </span>
              <p className="hint-text">
                Config parameters are your model's inputs.{' '}
                <TargetBlank href={DOC_URLS.config}>Learn more</TargetBlank>
              </p>
              <RunConfigTree config={config} />
            </Grid.Column>
            <Grid.Column>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}>
                <Header>Summary</Header>
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() =>
                    setShowRaw({
                      header: `Summary`,
                      json: getRawJsonDisplayText(summaryMetrics),
                    })
                  }>
                  View raw data
                </Button>
              </span>
              <p className="hint-text">
                Summary metrics are your model's outputs.{' '}
                <TargetBlank href={DOC_URLS.summaryMetric}>
                  Learn more
                </TargetBlank>
              </p>
              {_.keys(summaryMetrics).length === 0 ? (
                <Segment>
                  <p>No summary metrics saved for this run.</p>
                  <p>
                    Check the{' '}
                    <TargetBlank href={DOC_URLS.summaryMetric}>
                      summary metrics documentation
                    </TargetBlank>{' '}
                    for more information.
                  </p>
                </Segment>
              ) : (
                <RunSummaryTree summary={summaryMetrics} />
              )}
            </Grid.Column>
          </Grid.Row>
          <RunArtifactGrid
            entityName={projectEntityName}
            projectName={projectNameFromQuery}
            runName={runNameFromQuery}
          />
        </Grid>
      </div>
    </TailwindContents>
  );
}

export default React.memo(RunOverview);
